import React from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, Wrench, Users, CheckCircle, Clock, ArrowRight } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Shield,
      title: "Fire Hydrant System",
      description: "Complete fire hydrant installation and maintenance services for commercial and industrial facilities ensuring robust water supply.",
    },
    {
      icon: AlertTriangle,
      title: "Fire Alarm & Detection",
      description: "Advanced fire alarm and detection systems with smart sensors and 24/7 monitoring capabilities for early warning.",
    },
    {
      icon: Wrench,
      title: "Fire Sprinkler System",
      description: "Professional fire sprinkler system design, installation, and maintenance services for automatic suppression.",
    },
    {
      icon: Users,
      title: "Design & Consultancy",
      description: "Expert fire safety system design and consultancy services tailored for new constructions and facility upgrades.",
    },
    {
      icon: CheckCircle,
      title: "Annual Maintenance",
      description: "Comprehensive AMCs ensuring your fire safety systems remain operational, compliant, and ready at all times.",
    },
    {
      icon: Clock,
      title: "Fire NOC Consultant",
      description: "Professional end-to-end assistance in obtaining, managing, and renewing fire safety NOCs seamlessly.",
    }
  ];

  return (
    <section id="services" className="py-24 bg-dark relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1 rounded-full bg-white/5 text-primary font-semibold text-sm mb-6 uppercase tracking-wider border border-white/10"
          >
            Our Core Services
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6 font-barlow text-white uppercase tracking-tight"
          >
            Comprehensive <span className="text-primary">Solutions</span>
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg leading-relaxed"
          >
            From initial consultancy and design to installation and lifecycle maintenance, we provide end-to-end fire safety infrastructure for any scale.
          </motion.p>
        </div>

        {/* 3-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative"
              >
                {/* Card */}
                <div className="h-full p-8 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:border-primary/50 transition-colors duration-300 relative overflow-hidden flex flex-col">
                  {/* Red Top Border Accent */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                  
                  <div className="w-14 h-14 rounded-lg bg-dark border border-white/10 flex items-center justify-center mb-6 group-hover:bg-primary/10 transition-colors duration-300">
                    <IconComponent className="w-7 h-7 text-primary group-hover:text-accent transition-colors duration-300" />
                  </div>

                  <h3 className="text-xl font-bold mb-4 text-white font-barlow tracking-wide">
                    {service.title}
                  </h3>

                  <p className="text-gray-400 leading-relaxed mb-8 flex-grow">
                    {service.description}
                  </p>

                  <button className="flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-primary transition-colors mt-auto group-hover:gap-3 duration-300">
                    Explore Service <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
